/**
 * Per-pixel median + hybrid mix helpers for AI-Robust HDR fusion.
 *
 * Used at the coarsest pyramid level only (Codex 2.A.2.design D1+D4).
 * Replaces soft-blend output with a hybrid:
 *   final[px] = lerp(softBlend[px], median[px], mix[px])
 * where mix is high in regions where no bracket dominates (= weights are
 * near-uniform across brackets = noise-dominated). Median is a hard
 * outlier-rejector that kills uncorrelated AI-bracket noise without
 * touching higher-frequency Laplacian bands.
 *
 * Even-N convention (Codex D5 lock-in): lower-median `sorted[(N-1)>>1]`.
 * CPU and GPU MUST use the same convention for parity on ties — we pick
 * the lower index deterministically.
 *
 * Reference: G'MIC `iain_fast_median_stack` (exfusion3:6916-6918) is the
 * inspiration; this is a clean-room re-implementation in JS.
 */

/* Median of three via sort-network — 3 comparisons, no branches.
 *   median3(a, b, c) = max(min(a, b), min(max(a, b), c))
 * Constant-time, branch-free, exact on Float32. Matches the GPU shader
 * implementation in gpu/shaders.js:FRAG_MEDIAN_RGBA_3. */
function median3(a, b, c) {
    const lo = a < b ? a : b;
    const hi = a < b ? b : a;
    const mid = hi < c ? hi : c;
    return lo > mid ? lo : mid;
}

/**
 * Per-pixel median across N single-channel weight maps.
 *
 * @param {Float32Array[]} maps  array of N maps, each width*height
 * @param {number} n             pixel count = width * height
 * @returns {Float32Array}       new buffer of length n
 */
export function pixelMedianMaps(maps, n) {
    const N = maps.length;
    if (N === 0) throw new Error('pixelMedianMaps: empty stack');
    const out = new Float32Array(n);
    if (N === 1) { out.set(maps[0]); return out; }
    if (N === 3) {
        const a = maps[0], b = maps[1], c = maps[2];
        for (let i = 0; i < n; i++) out[i] = median3(a[i], b[i], c[i]);
        return out;
    }
    /* General N — selection by partial sort. Scratch reused across pixels. */
    const scratch = new Float32Array(N);
    const idx = (N - 1) >> 1;     /* lower-median for even N (Codex D5) */
    for (let i = 0; i < n; i++) {
        for (let k = 0; k < N; k++) scratch[k] = maps[k][i];
        scratch.sort();
        out[i] = scratch[idx];
    }
    return out;
}

/**
 * Per-pixel median across N RGBA Float32 stacks. Alpha is forced to 1.0
 * (matches fuseBrackets convention — fused alpha is undefined across
 * exposures). Each colour channel is medianed independently.
 *
 * @param {Float32Array[]} stacks  array of N rgba buffers, each w*h*4
 * @param {number} w
 * @param {number} h
 * @returns {Float32Array}         new buffer of length w*h*4
 */
export function pixelMedianRGBA(stacks, w, h) {
    const N = stacks.length;
    if (N === 0) throw new Error('pixelMedianRGBA: empty stack');
    const px = w * h;
    const out = new Float32Array(px * 4);
    if (N === 1) {
        const src = stacks[0];
        for (let i = 0; i < px; i++) {
            const p = i * 4;
            out[p] = src[p]; out[p + 1] = src[p + 1]; out[p + 2] = src[p + 2];
            out[p + 3] = 1;
        }
        return out;
    }
    if (N === 3) {
        const a = stacks[0], b = stacks[1], c = stacks[2];
        for (let i = 0; i < px; i++) {
            const p = i * 4;
            out[p]     = median3(a[p],     b[p],     c[p]);
            out[p + 1] = median3(a[p + 1], b[p + 1], c[p + 1]);
            out[p + 2] = median3(a[p + 2], b[p + 2], c[p + 2]);
            out[p + 3] = 1;
        }
        return out;
    }
    /* General N. */
    const scratch = new Float32Array(N);
    const idx = (N - 1) >> 1;
    for (let i = 0; i < px; i++) {
        const p = i * 4;
        for (let ch = 0; ch < 3; ch++) {
            for (let k = 0; k < N; k++) scratch[k] = stacks[k][p + ch];
            scratch.sort();
            out[p + ch] = scratch[idx];
        }
        out[p + 3] = 1;
    }
    return out;
}

/**
 * Hybrid-mix factor.
 *
 *   mix = clamp((1 - maxW) / (1 - 1/N), 0, 1)
 *
 * - maxW = max over brackets of normalized weight at this pixel
 * - mix = 0 when one bracket dominates entirely (maxW = 1) → keep soft blend
 * - mix = 1 when weights are uniform (maxW = 1/N)            → use median
 *
 * The (1 - 1/N) normalisation (Codex 2.A.2.design D1 correction) makes
 * the function reach 1 at uniform weights regardless of bracket count.
 * Without it, mix saturates lower for higher N.
 */
export function hybridMix(maxNormalizedWeight, N) {
    if (N <= 1) return 0;
    const denom = 1 - 1 / N;
    if (denom <= 0) return 0;
    const m = (1 - maxNormalizedWeight) / denom;
    if (m < 0) return 0;
    if (m > 1) return 1;
    return m;
}

/**
 * Per-pixel mix-map from a normalized-weight pyramid level.
 *
 * @param {Float32Array[]} normalizedWeights  array of N maps, each n long
 * @param {number} n                          pixel count
 * @returns {Float32Array}                    mix factor in [0,1] per pixel
 */
export function mixMapFromWeights(normalizedWeights, n) {
    const N = normalizedWeights.length;
    const out = new Float32Array(n);
    if (N <= 1) return out;        /* all zeros — no mix */
    const denom = 1 - 1 / N;
    if (denom <= 0) return out;
    for (let i = 0; i < n; i++) {
        let maxW = normalizedWeights[0][i];
        for (let k = 1; k < N; k++) {
            const v = normalizedWeights[k][i];
            if (v > maxW) maxW = v;
        }
        const m = (1 - maxW) / denom;
        out[i] = m < 0 ? 0 : (m > 1 ? 1 : m);
    }
    return out;
}

/**
 * Apply hybrid base-level blend in place on the soft-blend output.
 *
 *   softBlend[px] := lerp(softBlend[px], median[px], mix[px])
 *
 * Operates on RGBA buffers (3 colour channels, alpha untouched).
 *
 * @param {Float32Array} softBlend  rgba buffer, w*h*4 — MUTATED IN PLACE
 * @param {Float32Array} median     rgba buffer, w*h*4 — read-only
 * @param {Float32Array} mix        single-channel, w*h — read-only
 * @param {number} w
 * @param {number} h
 */
export function applyHybridBlendInPlace(softBlend, median, mix, w, h) {
    const px = w * h;
    for (let i = 0; i < px; i++) {
        const p = i * 4;
        const m = mix[i];
        if (m <= 0) continue;             /* fast path: keep soft blend */
        if (m >= 1) {
            softBlend[p]     = median[p];
            softBlend[p + 1] = median[p + 1];
            softBlend[p + 2] = median[p + 2];
            continue;
        }
        const inv = 1 - m;
        softBlend[p]     = inv * softBlend[p]     + m * median[p];
        softBlend[p + 1] = inv * softBlend[p + 1] + m * median[p + 1];
        softBlend[p + 2] = inv * softBlend[p + 2] + m * median[p + 2];
    }
}
